import { useEffect, useState } from "react";
import { api, addDaysISO, fmtDate } from "@/lib/api";
import { CheckCircle2, MinusCircle, Circle } from "lucide-react";

const STATUSES = [
  { key: "available", label: "Available", icon: CheckCircle2, tone: "border-emerald-300 bg-emerald-50 text-emerald-700" },
  { key: "flexible", label: "Flexible", icon: Circle, tone: "border-blue-300 bg-blue-50 text-brand" },
  { key: "unavailable", label: "Unavailable", icon: MinusCircle, tone: "border-slate-300 bg-slate-100 text-slate-500" },
];

export default function WorkerAvailability() {
  const [rows, setRows] = useState({});
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const days = Array.from({ length: 28 }, (_, i) => addDaysISO(i));

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/worker/availability");
      const map = {};
      data.forEach((r) => (map[r.date] = r));
      setRows(map);
    })();
  }, []);

  const update = (date, patch) =>
    setRows((r) => ({
      ...r,
      [date]: { date, start_time: "09:00", end_time: "18:00", availability_status: "available", ...(r[date] || {}), ...patch },
    }));

  const save = async () => {
    setBusy(true);
    setSaved(false);
    const entries = Object.values(rows).map((r) => ({
      date: r.date,
      start_time: r.start_time || "09:00",
      end_time: r.end_time || "18:00",
      availability_status: r.availability_status,
    }));
    await api.put("/worker/availability", { entries });
    setSaved(true);
    setBusy(false);
  };

  return (
    <div className="mx-auto max-w-4xl">
      <p className="overline">Availability</p>
      <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">My availability</h1>
      <p className="mt-3 text-sm text-slate-600">
        Tap a day to set how you are working it. Available days need a start and end time — that is what powers
        matching.
      </p>

      <div className="mt-8 space-y-3">
        {days.map((date) => {
          const row = rows[date];
          const status = row?.availability_status;
          return (
            <div
              key={date}
              className="card-surface flex flex-col gap-4 p-4 sm:flex-row sm:items-center"
              data-testid={`availability-row-${date}`}
            >
              <div className="min-w-[9rem]">
                <p className="font-display text-base font-medium">
                  {fmtDate(date, { weekday: "short", month: "short", day: "numeric" })}
                </p>
                <p className="text-xs text-slate-400">{date}</p>
              </div>

              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => update(date, { availability_status: s.key })}
                    data-testid={`availability-${date}-${s.key}`}
                    className={`pill border transition-colors ${
                      status === s.key ? s.tone : "border-slate-200 bg-white text-slate-500 hover:border-brand/40"
                    }`}
                  >
                    <s.icon className="h-3.5 w-3.5" /> {s.label}
                  </button>
                ))}
              </div>

              {status === "available" && (
                <div className="ml-auto flex items-center gap-2">
                  <input
                    type="time"
                    className="field-input w-32 py-1.5"
                    value={row?.start_time || "09:00"}
                    onChange={(e) => update(date, { start_time: e.target.value })}
                    data-testid={`availability-${date}-start`}
                  />
                  <span className="text-slate-400">–</span>
                  <input
                    type="time"
                    className="field-input w-32 py-1.5"
                    value={row?.end_time || "18:00"}
                    onChange={(e) => update(date, { end_time: e.target.value })}
                    data-testid={`availability-${date}-end`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="sticky bottom-4 mt-8 flex items-center gap-4 rounded-full border border-slate-200 bg-white/90 px-5 py-3 backdrop-blur">
        <button onClick={save} disabled={busy} className="btn-primary" data-testid="availability-save">
          {busy ? "Saving…" : "Save availability"}
        </button>
        {saved && (
          <span className="text-sm text-emerald-700" data-testid="availability-saved">
            Availability updated.
          </span>
        )}
      </div>
    </div>
  );
}
